import type React from 'react';
import { useState, useEffect, useCallback } from 'react';

import { dataSchema } from '@ragu-components/ord-types';
import { useReactFlow } from '@xyflow/react';
import { TextField } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import ResourceList from '../../components/ResourceList';
import SortableInstructionsList from '../../components/SortableInstructionsList';
import NodeControlForm from '../NodeControlForm';

import type { QueueInstruction, CallflowNodeInstruction, NodeControlsProps } from '../../dataProvider/types';
import type { Queue } from '@ragu-components/ord-types';

export interface QueueType extends Record<string, unknown> {
  queue: Queue | null;
  instructions: QueueInstruction[];
}

interface QueueControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: QueueType;
}

export default function QueueControls({ updateNodeData, data }: QueueControlsProps): React.ReactElement {
  const [queue, setQueue] = useState<Queue | null>(data.queue ?? null);
  const [instructions, setInstructions] = useState<QueueInstruction[]>(data.instructions ?? []);
  const reactFlowInstance = useReactFlow();

  const removeEdgesByHandles = useCallback(
    (handles: string[]) => {
      if (handles.length === 0 || reactFlowInstance === undefined) {
        return;
      }
      const edges = reactFlowInstance.getEdges();
      const filteredEdges = edges.filter((edge) => {
        if (edge.sourceHandle === undefined || edge.sourceHandle === null) {
          return true;
        }
        return !handles.includes(edge.sourceHandle);
      });
      if (filteredEdges.length !== edges.length) {
        flowchartLogger.debug('QueueControls: deleting edges for handles', { handles });
        reactFlowInstance.setEdges(filteredEdges);
      }
    },
    [reactFlowInstance]
  );

  const onQueueDataUpdate = (event: React.MouseEvent | React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('QueueControls: onQueueDataUpdate', { queue, instructions });
    if (queue === undefined || queue === null) {
      flowchartLogger.debug('QueueControls: queue is undefined or empty. Update cancelled.');
      return;
    }
    updateNodeData({ queue, instructions });
  };

  useEffect(() => {
    flowchartLogger.debug('QueueControls: autosave', { queue, instructions });
    updateNodeData({ queue, instructions });
  }, [queue, updateNodeData, instructions]);

  const updateIfValid = useCallback((): void => {
    if (
      // Have to check for null to avoid TS errors
      queue === undefined ||
      queue === null ||
      instructions === null ||
      instructions.length === 0 ||
      instructions.find(
        (instruction) => instruction.match_exit_causes === null || instruction.match_exit_causes.length === 0
      ) !== undefined
    ) {
      flowchartLogger.debug('QueueControls: invalid form data, update cancelled');
      return;
    }
    flowchartLogger.debug('QueueControls: node data updated');
    updateNodeData({ queue, instructions });
  }, [queue, instructions, updateNodeData]);

  const handleInstructionDelete = useCallback(
    (handleId: string) => {
      flowchartLogger.debug('QueueControls: removing instruction handle', { handleId });
      removeEdgesByHandles([handleId]);
      updateIfValid();
    },
    [removeEdgesByHandles, updateIfValid]
  );

  return (
    <NodeControlForm
      onSubmit={onQueueDataUpdate}
      isDisabled={queue === undefined || queue === null}
      data={data}
      updateNodeData={updateNodeData}
    >
      <SortableInstructionsList
        inputs={instructions}
        onChange={setInstructions as React.Dispatch<React.SetStateAction<CallflowNodeInstruction[]>>}
        onDelete={handleInstructionDelete}
        matchExitCauses={dataSchema.CallflowQueue.properties.connections.items.properties.match_exit_causes.items.enum.map(
          (choice) => choice
        )}
      />
      <ResourceList
        resource='queues'
        selectedResourceId={queue !== null ? queue.id : null}
        onRowClick={(_id, _resource, record) => {
          setQueue(record as Queue);
          return false;
        }}
      >
        <TextField source={'name'} />
        <TextField source={'description'} />
      </ResourceList>
    </NodeControlForm>
  );
}
